import axios from 'axios'

// Prefer env var; otherwise use same-origin '/api' (proxied in dev)
// Use same-origin proxy in dev; '/api' in production as well
const API_BASE = process.env.VUE_APP_API_BASE || 'https://api.marookebab.ir/api'
//const API_BASE = process.env.VUE_APP_API_BASE || 'http://localhost:5268/api'

// Helpful log once on startup
/* eslint-disable no-console */
console.log('[API] Base URL:', API_BASE)
/* eslint-enable no-console */

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE,
  timeout: 50000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Categories API
export const categoriesAPI = {
  // Get all categories
  getAll: () => api.get('/categories'),
  
  // Get category by ID
  getById: (id) => api.get(`/categories/${id}`),
  
  // Create new category
  create: (categoryData) => api.post('/categories', categoryData),
  
  // Update category
  update: (id, categoryData) => api.post(`/categories/${id}/update`, categoryData),
  
  // Delete category (soft delete)
  delete: (id) => api.post(`/categories/${id}/delete`),
  
  // Restore deleted category
  restore: (id) => api.post(`/categories/${id}/restore`),
  
  // Get deleted categories
  getDeleted: () => api.get('/categories/deleted'),
}

// Menu Items API
export const menuItemsAPI = {
  // Get all menu items (optionally filter by category)
  getAll: (categoryId = null) => {
    const params = categoryId ? { categoryId } : {}
    return api.get('/menuitems', { params })
  },
  
  // Get menu item by ID
  getById: (id) => api.get(`/menuitems/${id}`),
  
  // Get menu items by category
  getByCategory: (categoryId) => api.get(`/menuitems/category/${categoryId}`),
  
  // Create new menu item
  create: (itemData) => api.post('/menuitems', itemData),
  
  // Update menu item
  update: (id, itemData) => api.post(`/menuitems/${id}/update`, itemData),
  
  // Delete menu item (soft delete)
  delete: (id) => api.post(`/menuitems/${id}/delete`),
  
  // Restore deleted menu item
  restore: (id) => api.post(`/menuitems/${id}/restore`),
  
  // Get deleted menu items
  getDeleted: () => api.get('/menuitems/deleted'),
}

// Authentication API
export const authAPI = {
  // Login
  login: (credentials) => api.post('/auth/login', credentials),
  
  // Verify token
  verify: (token) => api.post('/auth/verify', token),
}

// Sections API
export const sectionsAPI = {
  // Get all sections
  getAll: () => api.get('/sections'),
  
  // Get section by ID
  getById: (id) => api.get(`/sections/${id}`),
  
  // Create new section
  create: (sectionData) => api.post('/sections', sectionData),
  
  // Update section
  update: (id, sectionData) => api.post(`/sections/${id}/update`, sectionData),
  
  // Delete section
  delete: (id) => api.post(`/sections/${id}/delete`),
}

// Employees API
export const employeesAPI = {
  // Get all employees
  getAll: () => api.get('/employees'),
  
  // Get employee by ID
  getById: (id) => api.get(`/employees/${id}`),
  
  // Create new employee
  create: (employeeData) => api.post('/employees', employeeData),
  
  // Update employee
  update: (id, employeeData) => api.post(`/employees/${id}/update`, employeeData),
  
  // Delete employee
  delete: (id) => api.post(`/employees/${id}/delete`),
}

// Customers API
export const customersAPI = {
  // Get all customers
  getAll: () => api.get('/customers'),

  // Get customer by ID
  getById: (id) => api.get(`/customers/${id}`),

  // Create new customer
  create: (customerData) => api.post('/customers', customerData),

  // Update customer
  update: (id, customerData) => api.put(`/customers/${id}`),

  // Delete customer
  delete: (id) => api.delete(`/customers/${id}`),
}

// Orders API
export const ordersAPI = {
  // Get all orders
  getAll: () => api.get('/orders'),
  
  // Get order by ID
  getById: (id) => api.get(`/orders/${id}`),
  
  // Get orders by table ID
  getByTable: (tableId) => api.get(`/orders/table/${tableId}`),
  
  // Get pickup orders
  getPickup: () => api.get('/orders/pickup'),
  
  // Create new order
  create: (orderData) => api.post('/orders', orderData),
  
  // Update order
  update: (id, orderData) => api.post(`/orders/${id}/update`, orderData),
  
  // Delete order
  delete: (id) => api.post(`/orders/${id}/delete`),
}

// Tax API
export const taxAPI = {
  // Get all taxes
  getAll: () => api.get('/tax'),
  
  // Get active tax
  getActive: () => api.get('/tax/active'),
  
  // Get tax by ID
  getById: (id) => api.get(`/tax/${id}`),
  
  // Create new tax
  create: (taxData) => api.post('/tax', taxData),
  
  // Update tax
  update: (id, taxData) => api.put(`/tax/${id}`, taxData),
  
  // Delete tax
  delete: (id) => api.delete(`/tax/${id}`),
}

// Upload API
export const uploadAPI = {
  // Upload single image
  uploadImage: (formData) => {
    return api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  
  // Upload multiple images
  uploadMultipleImages: (formData) => {
    return api.post('/upload/images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  // Delete image
  deleteImage: (fileName) => {
    return api.post(`/upload/image/${fileName}/delete`)
  },
}

export default api 